// src/components/landing/Features.js
import React, { useState } from 'react';
import { Clock, LineChart, Trophy, ChevronDown, ChevronUp, Brain, Battery, Zap, Settings } from 'lucide-react';

const Features = () => {
  const [expandedFeature, setExpandedFeature] = useState(null);
  const [activeTimer, setActiveTimer] = useState('pomodoro');

// Enhanced timer data with more in-depth information
const timers = {
    pomodoro: {
      name: 'Pomodoro Timer',
      icon: <Clock className="h-10 w-10 text-red-500" />,
      description: 'The classic productivity technique designed for maximum focus and mental freshness.',
      details: 'Work intensely for 25 minutes, then take a 5-minute break. After completing four work sessions, reward yourself with a longer 15-minute break. This science-backed technique leverages your brain\'s natural attention span to maximize productivity.',
      benefits: 'Ideal for breaking down large projects into manageable intervals, preventing mental fatigue, and creating a sense of urgency that improves focus. Research shows the short breaks prevent burnout while maintaining high levels of performance.',
      pattern: '25min work → 5min break → Repeat 4x → 15min long break',
      bestFor: 'Essays, problem sets, reading assignments, and tasks requiring consistent attention.',
      tips: 'Use the 5-minute breaks for quick physical activity like stretching to maximize cognitive recovery. Keep your phone away during the 25-minute work periods.'
    },
    flow: {
      name: 'Flow Timer',
      icon: <Brain className="h-10 w-10 text-purple-500" />,
      description: 'Designed to match your brain\'s natural rhythm for sustained concentration and deep work.',
      details: 'Enjoy 52-minute deep work sessions followed by 17-minute breaks - a scientifically optimized ratio based on productivity research. This extended format allows you to reach a state of "flow" where you become fully immersed in your work.',
      benefits: 'Perfect for complex tasks requiring deeper thinking and extended focus periods. The longer sessions allow you to get past the initial resistance and achieve breakthrough moments in understanding difficult concepts.',
      pattern: '52min work → 17min break → Repeat',
      bestFor: 'Complex problem-solving, creative work, research projects, and mastering difficult concepts.',
      tips: 'Start with a clear objective for each 52-minute session. Use the 17-minute breaks to completely disconnect - try meditation, light exercise, or a healthy snack.'
    },
    energy: {
      name: 'Energy Cycle Timer',
      icon: <Battery className="h-10 w-10 text-green-500" />,
      description: 'Based on the body\'s natural ultradian rhythm to maximize energy management throughout the day.',
      details: 'Work with your body\'s energy cycles with 90-minute focused sessions followed by 20-minute recovery periods. This approach aligns with your body\'s natural ultradian rhythm – the cycle of energy peaks and valleys throughout your day.',
      benefits: 'Helps maintain consistent performance throughout your study day by working with your natural energy oscillation. The 90-minute cycle matches your brain\'s natural transition from high alertness to fatigue, allowing you to study for longer periods without burnout.',
      pattern: '90min work → 20min break → Repeat',
      bestFor: 'Long study days, exam preparation periods, and marathon study sessions that require sustained energy.',
      tips: 'Schedule your most challenging subjects during the 90-minute sessions when your energy is highest. Use the 20-minute breaks for proper meals or power naps to fully recharge.'
    },
    peak: {
      name: 'Peak Performance Timer',
      icon: <Zap className="h-10 w-10 text-yellow-500" />,
      description: 'Optimized for high-intensity studying with carefully calibrated intervals for maximum information retention.',
      details: 'Achieve peak mental performance with 58-minute work intervals and 17-minute breaks. This scientifically-designed ratio optimizes cognitive performance for intensive learning and problem-solving.',
      benefits: 'Excellent for exam preparation and challenging subjects requiring intense focus. The specific timing leverages research on attention span peaks to help you absorb and retain complex information more effectively.',
      pattern: '58min work → 17min break → Repeat',
      bestFor: 'Final exam preparation, difficult technical subjects, and material requiring high recall ability.',
      tips: 'Use active recall techniques during these sessions. Take notes during the first half and review/test yourself during the second half of each 58-minute block.'
    },
    custom: {
      name: 'Custom Timers',
      icon: <Settings className="h-10 w-10 text-blue-500" />,
      description: 'Create your own personalized study schedule based on your unique concentration patterns and preferences.',
      details: 'Set custom work periods, break durations, and session counts to design your perfect study rhythm. Our intuitive interface lets you experiment with different timing combinations to find what works best for your brain.',
      benefits: 'Tailored to your personal learning style, schedule, and subject requirements. Custom timers allow you to adapt to your changing energy levels throughout the day or to specific types of coursework.',
      pattern: 'Flexible timing based on your preferences',
      bestFor: 'Students with unique attention spans, specialized study needs, or those who have found that standard timing methods don\'t work for them.',
      tips: 'Start by tracking your natural attention span over a few study sessions. Note when you begin to lose focus, then set your work interval just below that threshold. Experiment and adjust based on results.'
    }
  };
  
  // Enhanced Timer Content component
  const TimerContent = () => (
    <div className="bg-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center mb-4 text-gray-900">
          Smart Study Timer Options
        </h2>
        <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto">
          Choose the perfect timer based on your study style, subject complexity, and personal concentration patterns.
        </p>
  
        {/* Timer Selection Tabs */}
        <div className="flex flex-wrap justify-center mb-8 gap-2">
          {Object.keys(timers).map((key) => (
            <button
              key={key}
              onClick={() => setActiveTimer(key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTimer === key
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {timers[key].name}
            </button>
          ))}
        </div>
  
        {/* Active Timer Details */}
        <div className="bg-blue-50 rounded-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <div className="flex-shrink-0 bg-white p-4 rounded-full">
              {timers[activeTimer].icon}
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                {timers[activeTimer].name}
              </h3>
              <p className="text-gray-700 mb-4">
                {timers[activeTimer].description}
              </p>
              <p className="text-gray-600 mb-4">
                {timers[activeTimer].details}
              </p>
              <p className="text-gray-600 mb-4">
                <strong>Benefits:</strong> {timers[activeTimer].benefits}
              </p>
              <div className="bg-white p-3 rounded-md border border-blue-200 inline-block mb-4">
                <p className="text-blue-700 font-medium">
                  {timers[activeTimer].pattern}
                </p>
              </div>
              
              {/* New sections with additional helpful information */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg border border-blue-100">
                  <h4 className="font-medium text-lg mb-2 text-blue-800">Best For</h4>
                  <p className="text-gray-600">{timers[activeTimer].bestFor}</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-blue-100">
                  <h4 className="font-medium text-lg mb-2 text-blue-800">Pro Tips</h4>
                  <p className="text-gray-600">{timers[activeTimer].tips}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        {/* Additional information */}
        <div className="bg-gray-100 rounded-lg p-6 text-center">
          <p className="text-gray-700">
            All timers include notification sounds, visual countdowns, and session tracking to help you stay on course.
            <br />Try different timers to find which one works best for your unique study needs!
          </p>
        </div>
      </div>
    </div>
  );

  const features = {
    studyTimer: {
      title: "Smart Study Timer",
      description: "Stay focused with customizable study sessions and break reminders",
      icon: <Clock className="h-12 w-12 text-blue-600" />,
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      content: <TimerContent />
    },
    // Fix for the progress content section
    progress: {
        title: "Track Progress",
        description: "Stay on track with study timetables for progress tracking",
        icon: <LineChart className="h-12 w-12 text-green-600" />,
        bgColor: "bg-green-50",
        textColor: "text-green-600",
        content: (
          <div className="p-6 bg-green-50 rounded-lg mt-4">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Progress Tracking Tools</h3>
            <p className="text-gray-700 mb-4">
              Our comprehensive tracking system helps you visualize your study journey and stay motivated.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-medium text-lg mb-2">Smart Timetables</h4>
                <p className="text-gray-600">Generate optimized study timetables based on your courses, priorities, and available time to maximize productivity.</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-medium text-lg mb-2">Stats Analysis</h4>
                <p className="text-gray-600">Track total study hours, session frequency, and productivity trends with detailed analytics and visual reports.</p>
              </div>
            </div>
            
            <div className="bg-gray-100 p-4 rounded-lg">
              <p className="text-gray-700 text-center">
                All tracking tools sync across your devices so you can monitor your progress anywhere, anytime.
              </p>
            </div>
          </div>
        )
      },
    encouragement: {
      title: "Positive Encouragement",
      description: "Stay motivated with point, badges and tracking achievements to celebrate",
      icon: <Trophy className="h-12 w-12 text-orange-500" />,
      bgColor: "bg-orange-50",
      textColor: "text-orange-500",
      content: (
        <div className="p-6 bg-orange-50 rounded-lg mt-4">
          <h3 className="text-2xl font-semibold text-gray-900 mb-4">Achievement System</h3>
          <p className="text-gray-700 mb-4">
            Our gamified achievement system keeps you motivated throughout your academic journey.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-sm text-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-yellow-600 font-bold text-lg">🏆</span>
              </div>
              <h4 className="font-medium text-lg mb-2">Points & Rewards</h4>
              <p className="text-gray-600">Earn points for completed study sessions, streaks, and achieving goals.</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-blue-600 font-bold text-lg">🔶</span>
              </div>
              <h4 className="font-medium text-lg mb-2">Badges & Achievements</h4>
              <p className="text-gray-600">Unlock special badges for milestones like study streaks, subject mastery, and time management.</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-green-600 font-bold text-lg">📈</span>
              </div>
              <h4 className="font-medium text-lg mb-2">Progress Celebration</h4>
              <p className="text-gray-600">Receive encouragement at key milestones to keep your motivation high.</p>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-orange-200">
            <h4 className="font-medium text-lg mb-2 text-center">Weekly and Monthly Rewards</h4>
            <p className="text-gray-700 text-center">
              Stay consistent to earn special weekly rewards and monthly achievement certificates.
              Share your accomplishments with friends or keep a private record of your progress journey.
            </p>
          </div>
        </div>
      )
    }
  };

  const toggleFeature = (feature) => {
    if (expandedFeature === feature) {
      setExpandedFeature(null);
    } else {
      setExpandedFeature(feature);
    }
  };

  return (
    <div className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
          Features Designed for Students
        </h2>
        
        <div className="space-y-6">
          {/* Smart Study Timer */}
          <div>
            <div 
              className={`p-6 ${features.studyTimer.bgColor} rounded-lg flex justify-between items-center cursor-pointer hover:shadow-md transition-all duration-200`}
              onClick={() => toggleFeature('studyTimer')}
            >
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {features.studyTimer.title}
                </h3>
                <p className="text-gray-600">
                  {features.studyTimer.description}
                </p>
              </div>
              <div className="flex items-center">
                {features.studyTimer.icon}
                <div className="ml-2">
                  {expandedFeature === 'studyTimer' ? 
                    <ChevronUp className="h-6 w-6 text-gray-600" /> : 
                    <ChevronDown className="h-6 w-6 text-gray-600" />
                  }
                </div>
              </div>
            </div>
            {expandedFeature === 'studyTimer' && features.studyTimer.content}
          </div>

          {/* Track Progress */}
          <div>
            <div 
              className={`p-6 ${features.progress.bgColor} rounded-lg flex justify-between items-center cursor-pointer hover:shadow-md transition-all duration-200`}
              onClick={() => toggleFeature('progress')}
            >
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {features.progress.title}
                </h3>
                <p className="text-gray-600">
                  {features.progress.description}
                </p>
              </div>
              <div className="flex items-center">
                {features.progress.icon}
                <div className="ml-2">
                  {expandedFeature === 'progress' ? 
                    <ChevronUp className="h-6 w-6 text-gray-600" /> : 
                    <ChevronDown className="h-6 w-6 text-gray-600" />
                  }
                </div>
              </div>
            </div>
            {expandedFeature === 'progress' && features.progress.content}
          </div>

          {/* Positive Encouragement */}
          <div>
            <div 
              className={`p-6 ${features.encouragement.bgColor} rounded-lg flex justify-between items-center cursor-pointer hover:shadow-md transition-all duration-200`}
              onClick={() => toggleFeature('encouragement')}
            >
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {features.encouragement.title}
                </h3>
                <p className="text-gray-600">
                  {features.encouragement.description}
                </p>
              </div>
              <div className="flex items-center">
                {features.encouragement.icon}
                <div className="ml-2">
                  {expandedFeature === 'encouragement' ? 
                    <ChevronUp className="h-6 w-6 text-gray-600" /> : 
                    <ChevronDown className="h-6 w-6 text-gray-600" />
                  }
                </div>
              </div>
            </div>
            {expandedFeature === 'encouragement' && features.encouragement.content}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;